export type Size = { height: number; width: number };
export type Position = { x: number; y: number };
export type Transform = Position & Size;

/**
 * Returns a transform for the given element, relative to the viewport.
 * This is just a wrapper for `getBoundingClientRect`, whose name is incredibly
 * misleading (as `clientLeft`/`clientWidth` represent completely different
 * concepts).
 */
export function getElementTransform(element: HTMLElement): Transform {
  const rect = element.getBoundingClientRect();
  return getRectTransform(rect);
}

/**
 * Returns the transform for the given DOMRect or ClientRect.
 */
export function getRectTransform(
  rect: Record<"left" | "top", number> & Size,
): Transform {
  // This copies the object keys manually since `DOMRect|ClientRect` uses some
  // odd property rules that don't behave like regular objects.
  return {
    x: rect.left,
    y: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

/**
 * Get whether document elements can be reliably measured.
 */
export function canReliablyMeasureElements() {
  // Chrome's command for "Capture full size screenshot" temporarily collapses the document layout. Any component that relies an element's dimensions will observe that `el.clientWidth` is set to their absolute minimum, commonly `0`. A layout that relies on these measurements will produce incorrect results.
  // expected body bounding rect: `{"x":0,"y":0,"width":1,"height":1,"top":0,"right":1,"bottom":1,"left":0}`
  const body = document.body.getBoundingClientRect();
  const isChromeFullSizeScreenshotMode =
    body.x === 0 &&
    body.y === 0 &&
    body.width === 1 &&
    body.height === 1 &&
    body.top === 0 &&
    body.right === 1 &&
    body.bottom === 1 &&
    body.left === 0;

  return !isChromeFullSizeScreenshotMode;
}
